import React, {
    useCallback,
    useContext,
    useEffect,
    useReducer,
    useRef,
    useState,
} from "react"
import { ErrorBoundary } from "@sentry/react"
import classNames from "classnames"
import { AnimatePresence } from "motion/react"
import {
    LandscapeDisplay,
    SafeAreaProvider,
    useLandscapeDisplayInfo,
} from "@hg/layouts"
import { useShowRoleSelect } from "@hg/one-account"

import "@/utils/sentry"

import Layout from "@/components/Layout"
import EventBus from "@/utils/eventBus"
import { handleResizeBySize } from "@/utils/misc"
import { collect } from "@/utils/sdk"
import Cloud from "./components/Cloud"
import PageEnemy from "./pages/Enemy"
import PageGame from "./pages/Game"
import PageLoading from "./pages/Loading"
import PageLobby from "./pages/Lobby"
import PagePv from "./pages/PV"
import PageRecord from "./pages/Record"
import PageRelic from "./pages/Relic"
import { gameMusic, music } from "./utils/music"
import { SFX } from "./utils/sfx"
import {
    EPage,
    ESubPage,
    GlobalContext,
    globalReducer,
    initialGlobalState,
    IReducer,
} from "./context"
import { useTopicI18nStore } from "./locales"

import "@/theme/global.less"
import "./theme/global.less"
import styles from "./index.module.less"

const pages = [
    {
        key: EPage.Loading,
        component: PageLoading,
    },
    {
        key: EPage.Lobby,
        component: PageLobby,
    },
]

const subPages = [
    {
        key: ESubPage.Game,
        component: PageGame,
    },
]
if (!__ROGUE_5_PRE) {
    subPages.push(
        {
            key: ESubPage.PV,
            component: PagePv,
        },
        {
            key: ESubPage.Relic,
            component: PageRelic,
        },
        {
            key: ESubPage.Record,
            component: PageRecord,
        },
        {
            key: ESubPage.Enemy,
            component: PageEnemy,
        }
    )
}

const App = () => {
    const {
        state: { show, currentSubPage, bgm, assertLoaded, user },
        dispatch,
    } = useContext(GlobalContext)

    useEffect(() => {
        if (bgm) {
            SFX.enable()
            if (currentSubPage === ESubPage.Game) {
                music.disable()
                gameMusic.enable()
            } else {
                music.enable()
                gameMusic.disable()
            }
        } else {
            SFX.disable()
            gameMusic.disable()
            music.disable()
        }
    }, [bgm, currentSubPage])

    const [switching, setSwitching] = useState(false)

    const pageRef = useRef<HTMLDivElement>(null)
    const maskRef = useRef<HTMLDivElement>(null)

    const { width, height } = useLandscapeDisplayInfo()
    useEffect(() => {
        if (!width || !height) {
            return
        }

        const fontSize = handleResizeBySize(width, height, 16)
        dispatch({
            type: "updateContext",
            payload: { show: true, fontSize },
        })
    }, [dispatch, height, width])

    const handleNext = useCallback(
        async (current: EPage, next?: EPage) => {
            if (!next || switching) {
                return
            }

            setSwitching(true)

            const currentPage = document.querySelector(
                `.${current}`
            ) as HTMLDivElement

            const nextPage = document.querySelector(
                `.${next}`
            ) as HTMLDivElement

            window.setTimeout(() => {
                if (nextPage) {
                    nextPage.style.zIndex = "10"
                    nextPage.style.opacity = "1"
                }

                if (currentPage) {
                    currentPage.style.display = "none"
                }

                dispatch({
                    type: "updateContext",
                    payload: { currentPage: next },
                })

                setSwitching(false)
            }, 3000)
        },
        [dispatch, switching]
    )

    useEffect(() => {
        if (!user) {
            return
        }

        // 埋点
        collect("user_view", {
            channel_id: user.channelId,
            game_uid: user.uid,
        })
    }, [user])

    const showRoleSelect = useShowRoleSelect()

    useEffect(() => {
        EventBus.subscribe("showRoleSelect", showRoleSelect)

        return () => {
            EventBus.unsubscribe("showRoleSelect", showRoleSelect)
        }
    }, [showRoleSelect])

    const { LoginView } = useTopicI18nStore()

    const setCurrentSubPage = useCallback(
        (subPage: ESubPage | null) => {
            dispatch({
                type: "updateContext",
                payload: { currentSubPage: subPage },
            })

            if (bgm) {
                if (subPage === ESubPage.Game) {
                    music.disable()
                } else {
                    music.enable()
                }
            }
        },
        [bgm, dispatch]
    )

    return (
        <div className={classNames(styles.app, show && styles.show)}>
            <div ref={pageRef} className={styles.page}>
                {pages.map((page, index) => (
                    <page.component
                        key={page.key}
                        className={page.key}
                        style={{
                            zIndex: 10 - index,
                            opacity: page.key !== EPage.Loading ? 0 : 1,
                        }}
                        onNext={() =>
                            handleNext(page.key, pages[index + 1]?.key)
                        }
                    />
                ))}
            </div>
            {assertLoaded ? (
                <AnimatePresence>
                    {subPages.map(
                        (page) =>
                            currentSubPage === page.key && (
                                <page.component
                                    key={page.key}
                                    className={classNames(
                                        styles.subPage,
                                        page.key
                                    )}
                                    visible={true}
                                    onExit={() => setCurrentSubPage(null)}
                                />
                            )
                    )}
                </AnimatePresence>
            ) : null}
            <LoginView className={styles.pageRegistry} />
            <Cloud show={switching} />
            <div ref={maskRef} className={styles.mask} />
        </div>
    )
}

export const Root: React.FC = () => {
    const [globalState, dispatch] = useReducer<IReducer>(
        globalReducer,
        initialGlobalState
    )

    return (
        <ErrorBoundary>
            <GlobalContext.Provider
                value={{
                    state: globalState,
                    dispatch,
                }}
            >
                <Layout
                    context={{
                        type: globalState.type,
                        dispatch,
                    }}
                    needReload={false}
                >
                    <LandscapeDisplay>
                        <SafeAreaProvider>
                            <App />
                        </SafeAreaProvider>
                    </LandscapeDisplay>
                </Layout>
            </GlobalContext.Provider>
        </ErrorBoundary>
    )
}
